import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { LoadingMessage } from '@/components/ui/LoadingMessage'
import { useDismiss } from '@/hooks/useDismiss'
import { computePopoverPosition } from '@/utils/popover'
import { motion } from '@/utils/motion'
import { SheetHandle, sheetSafeArea, sheetSurface } from '@/components/ui/Sheet'
import { useSheetAnchor } from '@/hooks/useSheetAnchor'
import { cn } from '@/utils/cn'

interface TranslationPopoverProps {
  text: string
  translation: string | null
  loading: boolean
  anchorRect: DOMRect | null
  onClose: () => void
}

export function TranslationPopover({ text, translation, loading, anchorRect, onClose }: TranslationPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<CSSProperties>({ visibility: 'hidden', top: -9999, left: -9999 })

  useDismiss(popoverRef, onClose, anchorRect !== null)
  useSheetAnchor(anchorRect !== null, anchorRect, popoverRef)

  useEffect(() => {
    if (!anchorRect || !popoverRef.current) return
    const height = popoverRef.current.offsetHeight
    setPosition(computePopoverPosition(anchorRect, height))
  }, [anchorRect, loading, translation])

  useEffect(() => {
    if (anchorRect && popoverRef.current) void motion.enter(popoverRef.current, { dx: 0, ms: 180 })
  }, [anchorRect])

  if (!anchorRect) return null

  const style = position

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Translation"
      className={cn(
        'fixed z-50 sm:w-80 sm:max-w-80 max-h-[70vh] overflow-y-auto rounded-lg border border-border-divider bg-card shadow-lg',
        sheetSurface,
        sheetSafeArea,
      )}
      style={style}
    >
      <SheetHandle />
      <div className="p-4 space-y-3">
        <div>
          <p className="text-xs text-muted font-light uppercase tracking-wide mb-1">Original</p>
          <p className="text-sm text-body font-light leading-relaxed">{text}</p>
        </div>

        <div className="border-t border-border-divider pt-3">
          <p className="text-xs text-muted font-light uppercase tracking-wide mb-1">Translation</p>
          {loading && <LoadingMessage>Translating…</LoadingMessage>}
          {!loading && translation && (
            <p className="text-sm text-body font-light leading-relaxed">{translation}</p>
          )}
          {!loading && !translation && (
            <p className="text-sm text-muted font-light italic">Translation unavailable.</p>
          )}
        </div>
      </div>
    </div>
  )
}

